import { BaseControl } from '.';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { ControlsAccordionMenu } from './controlPrimitives';
import { EditorContext } from '../context/EditorContextValue';

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// NestedItemControl renders one collapsable accordion per nested item
// (output / event-field / indicator / question) that belongs to the
// widget's parent selection. Per-nested settings are persisted **keyed
// by stable nested-item ID**, not by array index — so reordering items
// upstream doesn't scramble which setting goes with which item.
//
// Value shape: { [nestedItemId]: { [childControlKey]: any } }

/**
 * @template TItem
 * @typedef {object} NestedItemControlOptions
 * @property {string} parentSettingKey - Widget setting key that holds the parent selection (e.g. 'activityId')
 * @property {(parentValue: any, widget: import('../widgets').BaseWidget) => Promise<Array<{id: string, label: string, item?: TItem}>>} nestedItemsResolver
 *   Resolves the list of nested items for a given parent selection.
 * @property {string} [emptyText] - Displayed when no nested items are available
 * @property {string} [noParentText] - Displayed when no parent is selected (defaults to hiding the control)
 */

/**
 * @extends {BaseControl<Object.<string, any>, NestedItemControlOptions>}
 */
export class NestedItemControl extends BaseControl {

    /**
     * @param {{
     *   label: string,
     *   description?: string,
     *   parentSettingKey: string,
     *   nestedItemsResolver: NestedItemControlOptions['nestedItemsResolver'],
     *   emptyText?: string,
     *   noParentText?: string,
     *   children: Object.<string, BaseControl>,
     * }} args
     */
    constructor({ label, description, parentSettingKey, nestedItemsResolver, emptyText, noParentText, children }) {
        super({
            label,
            description,
            defaultValue: /** @type {Object.<string, any>} */ ({}),
            children,
        });
        this.parentSettingKey = parentSettingKey;
        this.nestedItemsResolver = nestedItemsResolver;
        this.emptyText = emptyText || 'Geen items gevonden voor de huidige selectie.';
        this.noParentText = noParentText;
    }

    /**
     * @param {Object.<string, any>} value
     * @param {(value: Object.<string, any>) => void} onChange
     * @param {import('../widgets').BaseWidget} [widget]
     * @returns {React.ReactNode}
     */
    render(value, onChange, widget) {
        return (
            <NestedItemControlRenderer
                label={this.label}
                description={this.description}
                value={value ?? {}}
                onChange={onChange}
                controls={this.children}
                widget={widget}
                parentSettingKey={this.parentSettingKey}
                nestedItemsResolver={this.nestedItemsResolver}
                emptyText={this.emptyText}
                noParentText={this.noParentText}
            />
        );
    }
}

const NestedItemControlRenderer = ({
    label,
    description,
    value,
    onChange,
    controls,
    widget,
    parentSettingKey,
    nestedItemsResolver,
    emptyText,
    noParentText,
}) => {
    const [items, setItems] = useState(/** @type {Array<{id: string, label: string}>} */ ([]));
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    const parentValue = widget ? widget.getSetting(parentSettingKey) : null;

    // Auto-expand the accordion that matches the currently-viewed carousel
    // item on the canvas. The widget Renderer pushes that ID into EditorContext
    // via `setActiveNestedItemId`. EditorContext can be absent (e.g. in the
    // standalone analysis page) — then no auto-expand happens.
    const editorCtx = useContext(EditorContext);
    const activeFromCanvas = (editorCtx?.activeNestedItemIds || {})[widget?.id];

    // Locally track which accordion the user has manually toggled, so canvas
    // navigation doesn't fight a user-opened accordion. `null` means "no
    // manual override — follow the canvas".
    const [manualOpenId, setManualOpenId] = useState(/** @type {string|null} */ (null));
    const handleAccordionToggle = (itemId) => {
        setManualOpenId((cur) => (cur === itemId ? null : itemId));
    };
    const effectiveOpenId = manualOpenId !== null ? manualOpenId : activeFromCanvas;

    /* Latest widget for the resolver, without making it a dependency: every
       autosave delivers a NEW widget object via the snapshot, and depending on
       the object re-ran the resolver (Firestore reads) and unmounted the open
       accordion after each save. Re-resolve only when the widget or the parent
       choice changes. */
    const widgetRef = useRef(widget);
    useEffect(() => {
        widgetRef.current = widget;
    }, [widget]);
    const widgetId = widget?.id;

    useEffect(() => {
        if (!parentValue) {
            setItems([]);
            setLoadFailed(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setLoadFailed(false);
        Promise.resolve(nestedItemsResolver(parentValue, widgetRef.current))
            .then((result) => {
                if (cancelled) return;
                setItems(Array.isArray(result) ? result : []);
                setLoading(false);
            })
            .catch((error) => {
                console.error(`NestedItemControl: items for '${label}' failed to load`, error);
                if (cancelled) return;
                setItems([]);
                setLoadFailed(true);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [parentValue, nestedItemsResolver, widgetId, label]);

    if (!parentValue) {
        if (!noParentText) return null;
        return (
            <ControlsAccordionMenu label={label} isOpen={true}>
                <div className="nested-item-control">
                    <p className="nested-item-control-empty">{noParentText}</p>
                </div>
            </ControlsAccordionMenu>
        );
    }

    return (
        <ControlsAccordionMenu label={label} isOpen={true}>
            <div className="nested-item-control">
                {description && <p className="nested-item-control-description">{description}</p>}
                {loading && <p className="nested-item-control-loading">Laden...</p>}
                {!loading && loadFailed && (
                    <p role="alert" className="widget-control-load-error">
                        De items voor '{label}' konden niet worden geladen. Herlaad de pagina om het opnieuw te proberen.
                    </p>
                )}
                {!loading && !loadFailed && items.length === 0 && (
                    <p className="nested-item-control-empty">{emptyText}</p>
                )}
                {!loading && items.map((item) => (
                    <NestedItemAccordion
                        key={item.id}
                        item={item}
                        controls={controls}
                        value={value[item.id] || {}}
                        isOpen={item.id === effectiveOpenId}
                        onToggle={() => handleAccordionToggle(item.id)}
                        onChange={(itemValue) => onChange({ ...value, [item.id]: itemValue })}
                    />
                ))}
            </div>
        </ControlsAccordionMenu>
    );
};

const NestedItemAccordion = ({ item, controls, value, onChange, isOpen, onToggle }) => {
    return (
        <div className="nested-item">
            <button
                type="button"
                className="nested-item-toggle"
                onClick={onToggle}
                aria-expanded={isOpen}
            >
                <span className="nested-item-label">{item.label}</span>
                <span className="nested-item-arrow" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
            </button>
            {isOpen && (
                <div className="nested-item-content">
                    {Object.entries(controls).map(([key, control]) => {
                        // `visibleWhen` for a nested child reads the item's OWN
                        // settings (its sibling values), not the top-level widget's.
                        // The shim mirrors BaseWidget.getSetting's default fallback.
                        if (typeof control.visibleWhen === 'function'
                            && !control.visibleWhen({ getSetting: (k) => value[k] ?? controls[k]?.defaultValue })) {
                            return null;
                        }
                        return (
                            <Fragment key={key}>
                                {control.render(
                                    value[key] ?? control.defaultValue,
                                    (newVal) => onChange({ ...value, [key]: newVal })
                                )}
                            </Fragment>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
